import React, { useEffect, useReducer, useState } from "react";

import Table, { TableLoader } from "components/table";
import PaginationLength from "components/paginationLength";
import Tooltip, { TOOLTIP_POSITIONS } from "components/tooltip";
import RadioButton from "components/radioButton";
import WeAreSorry from "components/weAreSorry";
import NoResultCard from "components/noResultCard";
import Pagination from "components/pagination";

import { generateCancelToken, isRequestCancelled } from "interceptor/aeh";
import { API_FETCH_STATUS, PARTY_AGGREGATION_LEVEL } from "constants/common";
import MergeStateReducer from "utils/mergeStateReducer";
import usePartyAccounts from "hooks/usePartyAccounts";

import { DEFAULT_PARTY_ACCOUNTS_LIMIT, DEFAULT_PARTY_ACCOUNTS_OFFSET, VIEW_MODE } from "../constants/constants";
import IconLoader from "../IconLoader/loader";

import styles from "./partiesListContainer.scss";

const { LOADING, SUCCESS, ERROR } = API_FETCH_STATUS;
const { parties_list, total_account, totalcount, party_selector_footer, all_accounts_text } = styles;

let cancelToken;

const Header = ({ state, partyCount, product, partyAggregationLevel, lobDisplayName, partyUniqueCount }) => {
  if (state === LOADING) {
    return <IconLoader />;
  }
  const getPartyAccountHeading = () => {
    if (
      lobDisplayName &&
      (partyAggregationLevel === PARTY_AGGREGATION_LEVEL.LOB || partyAggregationLevel === PARTY_AGGREGATION_LEVEL.PARTY)
    ) {
      return " Total " + lobDisplayName + " Accounts Allocated to You";
    } else if (product && partyAggregationLevel === PARTY_AGGREGATION_LEVEL.PRODUCT) {
      return " Total " + product + " Accounts Allocated to You";
    } else {
      return " Total Accounts Allocated to You";
    }
  };
  return (
    <div className={totalcount}>
      <span>{partyUniqueCount}</span>
      {getPartyAccountHeading()}
    </div>
  );
};

const TableHead = () => {
  return (
    <thead>
      <tr>
        <th>{"  "}</th>
        <th>Account Details</th>
        <th>Product(s) Name</th>
        <th>Total Inventory</th>
      </tr>
    </thead>
  );
};

const InventoryList = ({ listOfInventory }) => {
  let result = "-";

  if (listOfInventory) {
    result = "";

    const noOfProducts = Object.keys(listOfInventory).length;

    Object.keys(listOfInventory).forEach((productName, index) => {
      const productCount = listOfInventory[productName];
      result += `${productName} (${productCount}) ${index === noOfProducts - 1 ? "" : ","} `;
    });

    result = result.length ? result : "No Active Products";
  }

  return result;
};

const PartiesList = ({ partyListState, selectedParty, setSelectedParty }) => {
  const { partyAccounts } = partyListState;

  if (partyListState.state === LOADING) {
    return <TableLoader colCount={4} />;
  }

  if (partyListState.state === ERROR) {
    return <WeAreSorry />;
  }

  const isSelectedParty = (index) => {
    if (!selectedParty) {
      return false;
    }
    if (selectedParty.partyId) {
      return selectedParty.partyId === partyAccounts[index].partyId && selectedParty.lob === partyAccounts[index].lob;
    }
    return selectedParty.partyName === partyAccounts[index].partyName;
  };

  return (
    <div className={parties_list}>
      {partyAccounts.length === 0 ? (
        <NoResultCard />
      ) : (
        <Table>
          <table>
            <TableHead />
            <tbody>
              {partyAccounts.map((partyDocument, index) => {
                const { partyId, partyName, lob, inventoryCount, totalProductInventory } = partyDocument;
                return (
                  <tr
                    key={`${partyId}_${index}`}
                    className={isSelectedParty(index) ? "b2b-lib-selected_row" : ""}
                    onClick={() => setSelectedParty(partyDocument)}
                  >
                    <td>
                      <div>
                        <RadioButton checked={isSelectedParty(index)} />
                      </div>
                    </td>
                    <td>
                      <div className="b2b-lib-td_label">Account Details</div>
                      <div className="b2b-lib-td_value">
                        <div className={all_accounts_text}>
                          {partyName}
                          {partyName === "All Accounts" && (
                            <Tooltip
                              position={TOOLTIP_POSITIONS.BOTTOM_START}
                              parent={<i className="aeh-icon-outlined-info"></i>}
                              content={<div>What is All Accounts? You can view all your party accounts by default.</div>}
                            />
                          )}
                        </div>
                      </div>
                      <div className="b2b-lib-td_subvalue">{partyId}</div>
                    </td>
                    <td>
                      <div className="b2b-lib-td_label">Product(s) Name</div>
                      <div className="b2b-lib-td_value">
                        <div className="b2b-lib-td_value">
                          {lob ? <InventoryList listOfInventory={inventoryCount} /> : "All Products"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="b2b-lib-td_label">Total Inventory</div>
                      <div className="b2b-lib-td_value">{totalProductInventory ?? "-"}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Table>
      )}
    </div>
  );
};

const Footer = ({ partyListState, setPartyListState }) => {
  const { totalFilterCount, rowCount, pageNo } = partyListState;

  const handlePaginationLengthChange = (value) => {
    setPartyListState({ rowCount: value, pageNo: 1 });
  };

  const handlePaginationChange = (value) => {
    setPartyListState({ pageNo: value });
  };

  if (partyListState.state === SUCCESS && totalFilterCount > 10 && rowCount) {
    return (
      <div className={party_selector_footer}>
        <PaginationLength
          value={rowCount}
          variant={"round"}
          totalCount={totalFilterCount}
          onChange={handlePaginationLengthChange}
        />
        <Pagination
          length={9}
          currentPage={pageNo}
          onPageSelect={handlePaginationChange}
          totalPages={Math.ceil(+totalFilterCount / +rowCount.key)}
        />
      </div>
    );
  }
  return null;
};

const getInitialState = (userPartyData) => {
  return {
    state: userPartyData.partyAccounts ? SUCCESS : LOADING,
    emailId: userPartyData.emailId,
    totalCount: userPartyData.totalCount,
    totalFilterCount: userPartyData.totalFilterCount,
    partyAccounts: userPartyData.partyAccounts,
    pageNo: DEFAULT_PARTY_ACCOUNTS_OFFSET,
    rowCount: DEFAULT_PARTY_ACCOUNTS_LIMIT,
    singlePartyUniqueCount: 0,
  };
};

const PartyListContainer = ({
  lob,
  lobDisplayName,
  product,
  selectedParty,
  setSelectedParty,
  partyAggregationLevel,
  partySelectorState,
  userPartyData,
  filterByLob,
  viewMode,
}) => {
  const [partyListState, setPartyListState] = useReducer(MergeStateReducer, getInitialState(userPartyData));
  const [uniqueCount, setUniqueCount] = useState(0);
  const { searchValue, searchType, partyUniqueCount } = partySelectorState;
  const { fetchPartyAccounts } = usePartyAccounts();

  const { rowCount, pageNo, emailId, totalFilterCount, totalCount, partyAccounts = [], singlePartyUniqueCount } = partyListState;

  useEffect(() => {
    if (viewMode === VIEW_MODE.ACCORDION) {
      setUniqueCount(singlePartyUniqueCount);
    } else {
      setUniqueCount(partyUniqueCount);
    }
  }, [singlePartyUniqueCount]);

  useEffect(() => {
    if (userPartyData) {
      setPartyListState({
        state: partySelectorState.state,
        emailId: userPartyData.emailId,
        totalCount: userPartyData.totalCount,
        partyAccounts: userPartyData.partyAccounts,
        totalFilterCount: userPartyData.totalFilterCount,
      });
    }
  }, [userPartyData]);

  useEffect(() => {
    if (partySelectorState.state !== LOADING && rowCount?.key) {
      setPartyListState({ state: LOADING });
      fetchPartyAccounts({ lob, emailId, rowCount, pageNo, partyAggregationLevel, searchType, searchValue, product })
        .then((response) => {
          if (response.data.data.userPartyData) {
            setPartyListState({
              ...response.data.data.userPartyData[0],
              singlePartyUniqueCount: response.data.data.partyUniqueCount,
              state: SUCCESS,
            });
          } else {
            throw new Error(response);
          }
        })
        .catch((error) => {
          if (!isRequestCancelled(error)) {
            setPartyListState({ state: ERROR });
            console.error(error);
          }
        });
    }
  }, [rowCount?.key, pageNo]);

  return (
    <div className={total_account}>
      <Header
        {...{
          product,
          filterByLob,
          lobDisplayName,
          partyAggregationLevel,
          state: partyListState.state,
          partyCount: searchValue || partyAggregationLevel === PARTY_AGGREGATION_LEVEL.PARTY ? totalFilterCount : totalCount,
          partyUniqueCount: uniqueCount,
        }}
      />
      <PartiesList {...{ partyListState, partyAggregationLevel, selectedParty, setSelectedParty, setPartyListState }} />
      <Footer {...{ partyListState, setPartyListState }} />
    </div>
  );
};

export default PartyListContainer;
